<template>
    <section id="home" class="hero-section">
        <img src="@/assets/images/hero/hero-main.jpg"
             alt="MMP Concrete LLC completed concrete project"
             class="hero-image" />

        <div class="hero-overlay"></div>

        <div class="hero-content">
            <p class="hero-eyebrow">
                <span></span>
                {{ t.hero.eyebrow }}
            </p>

            <h1>
                {{ t.hero.titleFirst }}
                <em>{{ t.hero.titleSecond }}</em>
            </h1>

            <p class="hero-description">
                {{ t.hero.description }}
            </p>

            <div class="hero-actions">
                <a class="primary-button" href="#contact">
                    {{ t.hero.estimate }}

                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M5 12h14"></path>
                        <path d="m13 6 6 6-6 6"></path>
                    </svg>
                </a>

                <a class="projects-link" href="#projects">
                    {{ t.hero.projects }}
                    <span aria-hidden="true">↓</span>
                </a>
            </div>
        </div>

        <!-- Trust messages -->
        <div class="trust-strip">
            <article v-for="item in t.hero.trust"
                     :key="item">
                <p>{{ item }}</p>
            </article>
        </div>

        <a class="scroll-indicator"
           href="#about"
           aria-label="Continue to About Us">
            <span></span>
        </a>
    </section>
</template>

<script>
    export default {
        name: 'HeroSection',

        props: {
            t: {
                type: Object,
                required: true
            }
        }
    }
</script>

<style scoped>
    .hero-section {
        position: relative;
        min-height: 780px;
        height: 100vh;
        max-height: 980px;
        overflow: hidden;
        color: var(--color-white);
        background-color: var(--color-charcoal);
    }

    .hero-image {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        animation: heroEntrance 1.3s ease-out both;
    }

    .hero-overlay {
        position: absolute;
        inset: 0;
        background: linear-gradient( 90deg, rgba(12, 12, 10, 0.92) 0%, rgba(12, 12, 10, 0.72) 38%, rgba(12, 12, 10, 0.24) 75%, rgba(12, 12, 10, 0.14) 100% ), linear-gradient( 0deg, rgba(12, 12, 10, 0.58) 0%, transparent 48% );
    }

    .hero-content {
        position: relative;
        z-index: 2;
        display: flex;
        width: min(840px, 67vw);
        min-height: 100%;
        padding: 180px 0 180px 7vw;
        justify-content: center;
        flex-direction: column;
    }

    .hero-eyebrow {
        display: flex;
        align-items: center;
        gap: 13px;
        margin-bottom: 25px;
        color: #efa46d;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.22em;
        text-transform: uppercase;
    }

        .hero-eyebrow span {
            width: 34px;
            height: 2px;
            background-color: currentColor;
        }

    h1 {
        max-width: 920px;
        margin: 0;
        font-family: var(--font-heading);
        font-size: clamp(64px, 7.4vw, 116px);
        font-weight: 400;
        letter-spacing: -0.045em;
        line-height: 0.88;
    }

        h1 em {
            display: block;
            color: #e48240;
            font-weight: 400;
        }

    .hero-description {
        max-width: 650px;
        margin: 35px 0;
        color: #e6e3dc;
        font-size: 18px;
        line-height: 1.7;
    }

    .hero-actions {
        display: flex;
        align-items: center;
        gap: 34px;
    }

    .primary-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 16px;
        padding: 18px 23px;
        color: var(--color-white);
        background-color: var(--color-orange);
        border: 1px solid var(--color-orange);
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.13em;
        text-transform: uppercase;
        transition: background-color 0.25s ease, border-color 0.25s ease, transform 0.25s ease;
    }

        .primary-button:hover {
            background-color: var(--color-orange-dark);
            border-color: var(--color-orange-dark);
            transform: translateY(-2px);
        }

        .primary-button svg {
            width: 18px;
            fill: none;
            stroke: currentColor;
            stroke-linecap: round;
            stroke-linejoin: round;
            stroke-width: 1.8;
        }

    .projects-link {
        padding: 10px 0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.55);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.08em;
        transition: border-color 0.25s ease;
    }

        .projects-link:hover {
            border-color: var(--color-orange);
        }

        .projects-link span {
            margin-left: 12px;
            color: var(--color-orange);
        }

    /* Trust strip */
    .trust-strip {
        position: absolute;
        z-index: 3;
        right: 0;
        bottom: 0;
        display: grid;
        width: min(65%, 950px);
        grid-template-columns: repeat(4, 1fr);
        background-color: rgba(24, 24, 21, 0.94);
        backdrop-filter: blur(10px);
    }

        .trust-strip article {
            display: flex;
            min-height: 100px;
            align-items: center;
            justify-content: center;
            padding: 25px 21px;
            border-left: 1px solid rgba(255, 255, 255, 0.13);
            text-align: center;
        }

        .trust-strip p {
            max-width: 180px;
            margin: 0;
            color: #ddd9d1;
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.11em;
            line-height: 1.55;
            text-transform: uppercase;
        }

    .scroll-indicator {
        position: absolute;
        z-index: 4;
        bottom: 35px;
        left: 3vw;
        display: flex;
        width: 26px;
        height: 45px;
        justify-content: center;
        border: 1px solid rgba(255, 255, 255, 0.4);
        border-radius: 20px;
    }

        .scroll-indicator span {
            width: 3px;
            height: 8px;
            margin-top: 9px;
            background-color: var(--color-orange);
            border-radius: 4px;
            animation: scrollIndicator 1.8s infinite;
        }

    @keyframes heroEntrance {
        from {
            opacity: 0;
            transform: scale(1.04);
        }

        to {
            opacity: 1;
            transform: scale(1);
        }
    }

    @keyframes scrollIndicator {
        0% {
            opacity: 0;
            transform: translateY(0);
        }

        30% {
            opacity: 1;
        }

        100% {
            opacity: 0;
            transform: translateY(17px);
        }
    }

    @media (max-width: 980px) {
        .hero-section {
            min-height: 780px;
            height: 100svh;
        }

        .hero-content {
            width: 100%;
            padding: 145px 25px 190px;
        }

        .hero-description {
            max-width: 570px;
            font-size: 16px;
        }

        .trust-strip {
            width: 100%;
            grid-template-columns: repeat(2, 1fr);
        }

            .trust-strip article {
                min-height: 58px;
                padding: 16px 19px;
            }

        .scroll-indicator {
            display: none;
        }
    }

    @media (max-width: 560px) {
        .hero-section {
            min-height: 790px;
        }

        .hero-image {
            object-position: 58% center;
        }

        .hero-overlay {
            background: linear-gradient( 90deg, rgba(12, 12, 10, 0.88), rgba(12, 12, 10, 0.47) ), linear-gradient( 0deg, rgba(12, 12, 10, 0.7), transparent 55% );
        }

        .hero-content {
            padding: 125px 20px 185px;
        }

        .hero-eyebrow {
            font-size: 9px;
        }

        h1 {
            font-size: clamp(53px, 16vw, 70px);
        }

        .hero-description {
            margin: 27px 0;
            font-size: 15px;
            line-height: 1.6;
        }

        .hero-actions {
            align-items: flex-start;
            gap: 15px;
            flex-direction: column;
        }

        .primary-button {
            width: 100%;
        }

        .trust-strip article {
            min-height: 55px;
            padding: 13px 10px;
        }

        .trust-strip p {
            max-width: 145px;
            font-size: 8px;
        }
    }
</style>